import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { Product, ProductVariant } from '../../types/product-variant';
import { useMedia } from '../../context/MediaContext';
import { orderProductMedia } from '../../utils/media';
import { cloudinaryUrl, cloudinaryVideoUrl } from '../../utils/cloudinary';

interface MobileProductGalleryProps {
    product: Product;
    selectedVariant: ProductVariant | null;
    onImageClick: (imageSrc: string) => void;
}

interface MediaItem {
    type: 'image' | 'video';
    src: string;
}

const MobileProductGallery = ({ product, selectedVariant, onImageClick }: MobileProductGalleryProps) => {
    const [heroIndex, setHeroIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const isDragging = useRef(false);

    const { getMediaByProduct } = useMedia();

    const resolveProductMedia = () => {
        let media = product.airtableId ? getMediaByProduct(product.airtableId) : [];
        if (media.length === 0 && product.id) media = getMediaByProduct(product.id);
        if (media.length === 0 && product.slug) media = getMediaByProduct(product.slug);
        return media;
    };

    const productMedia = resolveProductMedia();
    const ordered = orderProductMedia(productMedia);
    // Variant images override media when a variant is selected
    const variantImages: string[] = selectedVariant?.images
        ? (Array.isArray(selectedVariant.images)
            ? selectedVariant.images
            : [selectedVariant.images.main, selectedVariant.images.model, selectedVariant.images.zoom].filter(Boolean) as string[])
        : [];

    // Unified media list — order comes from usage tags in DB
    const allMedia: MediaItem[] = (variantImages.length > 0
        ? variantImages.map(src => ({ type: (src.includes('/video/upload/') ? 'video' : 'image') as 'image' | 'video', src }))
        : ordered.map(m => ({ type: (m.mediaType ?? 'image') as 'image' | 'video', src: m.cloudinaryURL! }))
    ).filter((m, i, arr) => m.src && arr.findIndex(x => x.src === m.src) === i);

    // Hero: first 4 items (or all if fewer)
    const heroMedia = allMedia.slice(0, 4);
    // Additional: everything after 4
    const additionalMedia = allMedia.slice(4);

    // --- Swipe handling ---
    const goTo = useCallback((index: number) => {
        setDirection(index > heroIndex ? 1 : -1);
        setHeroIndex(index);
    }, [heroIndex]);

    const handleDragEnd = useCallback((_: unknown, info: PanInfo) => {
        const threshold = 50;
        const velocityThreshold = 300;

        if (info.offset.x < -threshold || info.velocity.x < -velocityThreshold) {
            if (heroIndex < heroMedia.length - 1) goTo(heroIndex + 1);
        } else if (info.offset.x > threshold || info.velocity.x > velocityThreshold) {
            if (heroIndex > 0) goTo(heroIndex - 1);
        }

        setTimeout(() => { isDragging.current = false; }, 100);
    }, [heroIndex, heroMedia.length, goTo]);

    const handleTap = (src: string) => {
        if (!isDragging.current) onImageClick(src);
    };

    const variants = {
        enter: (dir: number) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (dir: number) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0 }),
    };

    return (
        <>
            {/* ===== Hero Gallery ===== */}
            <div className="relative w-full aspect-square overflow-hidden bg-white dark:bg-brand-dark">
                {/* Preload all hero images so swipes are instant */}
                <div className="hidden" aria-hidden="true">
                    {heroMedia.map((m, i) =>
                        m.type === 'image' && i !== heroIndex ? (
                            <img key={i} src={cloudinaryUrl(m.src, 600)} alt="" />
                        ) : null
                    )}
                </div>

                <AnimatePresence initial={false} custom={direction} mode="popLayout">
                    <motion.div
                        key={heroIndex}
                        custom={direction}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        drag="x"
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={0.15}
                        onDragStart={() => { isDragging.current = true; }}
                        onDragEnd={handleDragEnd}
                        className="absolute inset-0"
                    >
                        {heroMedia[heroIndex]?.type === 'video' ? (
                            <video
                                src={cloudinaryVideoUrl(heroMedia[heroIndex].src)}
                                className="w-full h-full object-cover"
                                autoPlay
                                loop
                                muted
                                playsInline
                                onClick={() => handleTap(heroMedia[heroIndex].src)}
                            />
                        ) : (
                            <img
                                src={cloudinaryUrl(heroMedia[heroIndex]?.src, 600)}
                                alt={`${product.name} - ${heroIndex + 1}`}
                                className="w-full h-full object-cover"
                                draggable={false}
                                onClick={() => handleTap(heroMedia[heroIndex].src)}
                            />
                        )}
                    </motion.div>
                </AnimatePresence>

                {/* Dot indicators */}
                {heroMedia.length > 1 && (
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-10 pointer-events-none">
                        {heroMedia.map((_, i) => (
                            <div
                                key={i}
                                className={`h-2 rounded-full transition-all duration-300 ${
                                    i === heroIndex
                                        ? 'w-6 bg-brand-blue'
                                        : 'w-2 bg-white/60'
                                }`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};

/** Standalone section for additional media below ProductInfo */
export const MobileAdditionalMedia = ({ product, selectedVariant, onImageClick }: MobileProductGalleryProps) => {
    const { getMediaByProduct } = useMedia();

    const resolveProductMedia = () => {
        let media = product.airtableId ? getMediaByProduct(product.airtableId) : [];
        if (media.length === 0 && product.id) media = getMediaByProduct(product.id);
        if (media.length === 0 && product.slug) media = getMediaByProduct(product.slug);
        return media;
    };

    const productMedia = resolveProductMedia();
    const ordered = orderProductMedia(productMedia);
    const variantImages: string[] = selectedVariant?.images
        ? (Array.isArray(selectedVariant.images)
            ? selectedVariant.images
            : [selectedVariant.images.main, selectedVariant.images.model, selectedVariant.images.zoom].filter(Boolean) as string[])
        : [];

    // Unified media list — order comes from usage tags in DB
    const allMedia: MediaItem[] = (variantImages.length > 0
        ? variantImages.map(src => ({ type: (src.includes('/video/upload/') ? 'video' : 'image') as 'image' | 'video', src }))
        : ordered.map(m => ({ type: (m.mediaType ?? 'image') as 'image' | 'video', src: m.cloudinaryURL! }))
    ).filter((m, i, arr) => m.src && arr.findIndex(x => x.src === m.src) === i);

    const additionalMedia = allMedia.slice(4);

    if (additionalMedia.length === 0) return null;

    return (
        <div className="space-y-1">
            {additionalMedia.map((media, i) => (
                <div
                    key={i}
                    className="w-full cursor-pointer"
                    onClick={() => onImageClick(media.src)}
                >
                    {media.type === 'video' ? (
                        <video
                            src={cloudinaryVideoUrl(media.src)}
                            className="w-full object-cover"
                            autoPlay
                            loop
                            muted
                            playsInline
                        />
                    ) : (
                        <img
                            src={cloudinaryUrl(media.src, 600)}
                            alt={`${product.name} - additional view ${i + 1}`}
                            className="w-full object-cover"
                            loading="lazy"
                        />
                    )}
                </div>
            ))}
        </div>
    );
};

export default MobileProductGallery;
